import React from 'react';
import { useNavigate } from 'react-router-dom';
import SharedFilters from '../../../components/SharedFilters';
import AppActionMenu from '../../../components/AppActionMenu';
import AppPagination from '../../../components/AppPagination';
import { useBrandListScreen } from './hooks/useBrandListScreen';
import { appToast } from '../../../components/AppToast';

export default function BrandListScreen() {
  const { state, actions } = useBrandListScreen();
  const navigate = useNavigate();

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-5">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Brand Registry
          </h1>
          <p className="mt-2 text-sm text-on-surface-variant max-w-xl leading-relaxed">
            Configure and manage product brands, manufacturer labels, and inventory segments.
          </p>
        </div>
      </section>

      {/* Shared Filters Component */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        statusFilter={state.statusFilter}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setStatusFilter={actions.setStatusFilter}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        onReset={actions.resetFilters}

        searchFieldOptions={[{ value: 'Name', label: 'Brand Name' }]}
        statusOptions={[{ value: 'Active', label: 'Active' }, { value: 'Archive', label: 'Archived' }, { value: 'All', label: 'All' }]}
        companyOptions={[{ value: 'All', label: 'All Companies' }]}
        branchOptions={[{ value: 'All', label: 'All Branches' }]}
        departmentOptions={[{ value: 'All', label: 'All Departments' }]}
        visibleAccordionFilters={['company', 'branch', 'department']}
        tools={[
          { key: 'download', label: 'Download', icon: 'download' },
          { key: 'upload-data', label: 'Upload Data', icon: 'upload' }
        ]}
        onToolSelect={(key) => {
          if (key === 'download') {
            actions.triggerExport({
              viewMode: state.statusFilter,
              searchText: state.searchText,
            });
          }
        }}
      />


      {/* Data Table Card */}
      <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest overflow-hidden shadow-sm flex flex-col">
        
        {/* Header Row */}
        <div className="grid grid-cols-12 px-8 py-3.5 border-b border-outline-variant/25 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant select-none">
          <div className="col-span-8">Brand Details</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>

        {/* Data Rows */}
        <div className="flex flex-col">
          {state.isLoading ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              Loading brands...
            </div>
          ) : state.rows.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-on-surface-variant font-medium">
              No brands found matching filters.
            </div>
          ) : (
            state.rows.map((row) => (
              <div 
                key={row.id} 
                className="grid grid-cols-12 items-center px-8 py-4 border-b border-outline-variant/10 hover:bg-surface-container-low/40 transition-colors last:border-b-0"
              >
                {/* Brand Details */}
                <div className="col-span-8 flex items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#dae2ff]/40 text-primary">
                    <span className="material-symbols-outlined">sell</span>
                  </div>
                  <div>
                    <p className="text-base font-bold text-on-surface select-text">{row.name}</p>
                    <p className="text-xs text-on-surface-variant font-medium select-text">ID: {row.code}</p>
                  </div>
                </div>

                {/* Status chip */}
                <div className="col-span-2 flex select-none justify-start">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${
                      row.status === 'Active' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-surface-variant text-on-surface-variant'
                    }`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${
                      row.status === 'Active' ? 'bg-emerald-500' : 'bg-outline'
                    }`} />
                    {row.status}
                  </span>
                </div>

                {/* Actions */}
                <div className="col-span-2 flex justify-end select-none">
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                    items={[
                      { key: 'edit', label: 'Edit', icon: 'edit' },
                      { key: 'archive', label: row.status === 'Active' ? 'Archive' : 'Unarchive', icon: row.status === 'Active' ? 'archive' : 'unarchive' },
                      { key: 'delete', label: 'Delete', icon: 'delete' },
                      { key: 'branchwise-stock', label: 'Branchwise Stock', icon: 'inventory' },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        appToast.info('Edit option is currently disabled.');
                      } else if (key === 'archive') {
                        actions.handleArchive(row.id);
                      } else if (key === 'delete') {
                        actions.handleDelete(row.id);
                      } else if (key === 'branchwise-stock') {
                        appToast.info(`Opening Branchwise Stock report for brand "${row.name}"`);
                      }
                    }}
                  />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer Control */}
        <div className="flex items-center justify-between bg-surface-container px-6 py-4 text-sm text-on-surface-variant font-semibold select-none border-t border-outline-variant/10">
          <span>
            Showing {((state.page - 1) * state.pageSize) + 1} to {Math.min(state.page * state.pageSize, state.meta.totalCount)} of {state.meta.totalCount} entries
          </span>
          
          <AppPagination
            page={state.page}
            totalPages={Math.ceil(state.meta.totalCount / state.pageSize) || 1}
            onPageChange={actions.setPage}
            onPrevious={() => actions.setPage(p => Math.max(p - 1, 1))}
            onNext={() => actions.setPage(p => Math.min(p + 1, Math.ceil(state.meta.totalCount / state.pageSize)))}
            canPrevious={state.page > 1}
            canNext={state.page < Math.ceil(state.meta.totalCount / state.pageSize)}
          />
        </div>

      </div>
    </div>
  );
}
